import { useEffect, useState } from 'react';

/**
 * 成图要导出成 PNG，图片必须能跨域读取（COS 需允许本站跨域）。
 * 这里统一用 crossOrigin 预加载：读得到就用，读不到就退回分类色块，
 * 免得导出时 canvas 被污染整张失败。
 * 浏览器可能缓存过不带跨域头的同一张图，第一次失败后加个参数再试一次。
 */
const cache = new Map<string, Promise<string | null>>();
// 已有结果的同步读出，离屏导出时第一次渲染就能带上图片
const resolved = new Map<string, string | null>();

function tryLoad(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

const withRetryParam = (url: string) => `${url}${url.includes('?') ? '&' : '?'}xhs=1`;

export function loadPosterImage(src: string): Promise<string | null> {
  let pending = cache.get(src);
  if (!pending) {
    pending = (async () => {
      if (await tryLoad(src)) return src;
      if (src.startsWith('data:') || src.startsWith('blob:')) return null;
      const retry = withRetryParam(src);
      return (await tryLoad(retry)) ? retry : null;
    })().then((url) => {
      resolved.set(src, url);
      return url;
    });
    cache.set(src, pending);
  }
  return pending;
}

/** 返回能用于导出的地址；undefined 表示还在加载，null 表示读不到 */
export function usePosterImage(src: string | null | undefined): string | null | undefined {
  const [state, setState] = useState<{ src: string | null | undefined; url: string | null | undefined }>({
    src,
    url: src ? resolved.get(src) : null,
  });
  useEffect(() => {
    if (!src) {
      setState({ src, url: null });
      return;
    }
    if (resolved.has(src)) {
      setState({ src, url: resolved.get(src) });
      return;
    }
    let alive = true;
    setState({ src, url: undefined });
    void loadPosterImage(src).then((url) => {
      if (alive) setState({ src, url });
    });
    return () => {
      alive = false;
    };
  }, [src]);
  return state.src === src ? state.url : src ? resolved.get(src) : null;
}

/** 导出前等这些图都加载完，返回读不到的张数 */
export async function preloadPosterImages(urls: Array<string | null | undefined>): Promise<number> {
  const results = await Promise.all(
    Array.from(new Set(urls.filter((url): url is string => Boolean(url)))).map(loadPosterImage),
  );
  return results.filter((url) => url === null).length;
}
